import { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

// Default center over Tbilisi when the drawer opens without coords set.
const DEFAULT_CENTER = { lat: 41.7151, lng: 44.8271 };

const containerStyle = {
  width: '100%',
  height: 'clamp(200px, 32vw, 320px)',
  borderRadius: '12px',
};

// Constant id so the loader deduplicates when multiple LocationPickerMap
// instances mount (e.g. Create + Edit drawers on the same page).
const LOADER_ID = 'gzavnili-pickup-location-map';

const LocationPickerMap = ({ latitude, longitude, onChange, defaultCenter }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const geocoderRef = useRef(null);

  // useJsApiLoader is a hook-based, singleton loader — safe to call from any
  // number of components. It loads the script once per page and returns
  // isLoaded on every re-render, unlike <LoadScript /> which throws on
  // remount ("google api is already presented").
  const { isLoaded, loadError } = useJsApiLoader({
    id: LOADER_ID,
    googleMapsApiKey: apiKey || '',
  });

  const parsedLat = latitude === '' || latitude == null ? null : Number(latitude);
  const parsedLng =
    longitude === '' || longitude == null ? null : Number(longitude);

  const hasMarker =
    parsedLat != null &&
    parsedLng != null &&
    !Number.isNaN(parsedLat) &&
    !Number.isNaN(parsedLng);

  const center = useMemo(
    () =>
      hasMarker ? { lat: parsedLat, lng: parsedLng } : defaultCenter,
    [hasMarker, parsedLat, parsedLng, defaultCenter]
  );

  // Reverse-geocode the picked coordinate into a street-address string using
  // Google's Geocoder. Falls back to null on any error so the caller can keep
  // the previous address unchanged.
  const reverseGeocode = useCallback((lat, lng) => {
    if (!window.google?.maps?.Geocoder) return Promise.resolve(null);
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    return new Promise((resolve) => {
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          resolve(results[0].formatted_address);
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  const emitPosition = useCallback(
    async (lat, lng) => {
      // Keep 6 decimals to match the entity column precision (decimal 10,6).
      const latStr = lat.toFixed(6);
      const lngStr = lng.toFixed(6);
      const address = await reverseGeocode(lat, lng);
      onChange({
        latitude: latStr,
        longitude: lngStr,
        address, // may be null; parent can decide whether to overwrite
      });
    },
    [onChange, reverseGeocode]
  );

  const handleMapClick = useCallback(
    (e) => {
      emitPosition(e.latLng.lat(), e.latLng.lng());
    },
    [emitPosition]
  );

  const handleMarkerDragEnd = useCallback(
    (e) => {
      emitPosition(e.latLng.lat(), e.latLng.lng());
    },
    [emitPosition]
  );

  const renderPlaceholder = (label) => (
    <div
      style={{
        ...containerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f4f6f8',
        color: '#637381',
        fontSize: 13,
        padding: 16,
        textAlign: 'center',
      }}
    >
      {label}
    </div>
  );

  if (!apiKey) {
    return renderPlaceholder('REACT_APP_GOOGLE_MAPS_API_KEY is not set in .env');
  }

  if (loadError) {
    return renderPlaceholder('Could not load Google Maps. Please try again.');
  }

  if (!isLoaded) {
    return renderPlaceholder('Loading map…');
  }

  return (
    <div style={{ width: '100%' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={hasMarker ? 15 : 12}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
        }}
      >
        {hasMarker && (
          <Marker
            position={{ lat: parsedLat, lng: parsedLng }}
            draggable
            onDragEnd={handleMarkerDragEnd}
          />
        )}
      </GoogleMap>
      <div
        style={{
          fontSize: 12,
          color: '#637381',
          marginTop: 6,
          textAlign: 'center',
        }}
      >
        Click on the map to place a pin, or drag the pin to adjust.
      </div>
    </div>
  );
};

LocationPickerMap.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  defaultCenter: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
};

LocationPickerMap.defaultProps = {
  defaultCenter: DEFAULT_CENTER,
};

export default LocationPickerMap;
